/**
 * Phase J.4a (2026-07-29) — /customer/activate-ach?token=...
 *
 * Public (no JWT) landing page that:
 *   1. Reads the ?token=... query param.
 *   2. POSTs to /api/customer/ach-activation/consume to swap it for a
 *      Stripe SetupIntent client_secret + publishable key.
 *   3. Renders Stripe Elements (mode="setup", payment_method_types=["us_bank_account"])
 *      inline so the customer can complete Financial Connections.
 *   4. On success, shows a confirmation and links back to their portal.
 */
import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
    Elements,
    PaymentElement,
    useElements,
    useStripe,
} from "@stripe/react-stripe-js";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, AlertTriangle, Landmark, ShieldCheck, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function fmtErr(err) {
    const d = err?.response?.data?.detail;
    if (typeof d === "string") return d;
    return err?.message || "Something went wrong.";
}


function ActivateInner({ boot, onSuccess }) {
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [ready, setReady] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements || submitting) return;
        setSubmitting(true);
        setError(null);
        try {
            const returnUrl = `${window.location.origin}/customer/activate-ach?done=1`;
            const result = await stripe.confirmSetup({
                elements,
                confirmParams: { return_url: returnUrl },
                // For hosted-page fallback we want redirect; but for
                // instant-verification via Financial Connections Stripe
                // does the popup automatically.
            });
            if (result?.error) {
                setError(result.error.message || "We couldn't verify your bank. Please try again.");
            } else {
                // Some flows don't redirect (instant FC) — mark success.
                onSuccess && onSuccess();
            }
        } catch (err) {
            setError(err?.message || "Unexpected error verifying bank.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={submit} className="space-y-4" data-testid="activate-ach-form">
            <div className="rounded-lg border border-slate-200 bg-white p-3">
                <PaymentElement
                    options={{
                        layout: "tabs",
                        paymentMethodOrder: ["us_bank_account"],
                        wallets: { applePay: "never", googlePay: "never" },
                    }}
                    onReady={() => setReady(true)}
                    onLoadError={(ev) => setError(ev?.error?.message || "Stripe Elements failed to load.")}
                />
            </div>
            <div className="rounded-md bg-slate-50 ring-1 ring-slate-200 p-3 text-xs text-slate-600 leading-relaxed">
                <p className="flex items-start gap-2">
                    <ShieldCheck size={14} className="mt-0.5 text-emerald-700 shrink-0" />
                    <span>
                        You&apos;re verifying <strong>{boot.company_name || "your account"}</strong>&apos;s bank via Stripe.
                        Once verified, invoices for this company can be paid automatically on their due date.
                    </span>
                </p>
            </div>
            {error && (
                <div className="rounded-md bg-red-50 ring-1 ring-red-200 p-3 text-sm text-red-800 flex items-start gap-2" data-testid="activate-ach-error">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                    <span>{error}</span>
                </div>
            )}
            <Button
                type="submit"
                disabled={!stripe || !elements || submitting || !ready}
                className="bg-blue-900 hover:bg-blue-800 gap-2 w-full"
                data-testid="activate-ach-submit"
            >
                {submitting ? (<><Loader2 size={14} className="animate-spin" /> Verifying bank…</>) : "Verify bank & finish setup"}
            </Button>
        </form>
    );
}


export default function CustomerActivateAchPage() {
    const [params] = useSearchParams();
    const token = params.get("token");
    const alreadyDone = params.get("done") === "1";
    const [state, setState] = useState({
        loading: true, error: null, clientSecret: null, stripePromise: null,
        company_name: null, customer_email_hint: null,
    });
    const [succeeded, setSucceeded] = useState(alreadyDone);

    useEffect(() => {
        if (alreadyDone) {
            setState((s) => ({ ...s, loading: false }));
            return;
        }
        if (!token) {
            setState({ loading: false, error: "Missing activation token in the link.",
                       clientSecret: null, stripePromise: null,
                       company_name: null, customer_email_hint: null });
            return;
        }
        (async () => {
            try {
                const { data } = await axios.post(
                    `${BACKEND_URL}/api/customer/ach-activation/consume`,
                    { token },
                );
                if (!data?.setup_intent_client_secret || !data?.publishable_key) {
                    throw new Error("Stripe SetupIntent not configured. Contact support.");
                }
                setState({
                    loading: false, error: null,
                    clientSecret: data.setup_intent_client_secret,
                    stripePromise: loadStripe(data.publishable_key),
                    company_name: data.company_name,
                    customer_email_hint: data.customer_email_hint,
                });
            } catch (err) {
                setState({ loading: false, error: fmtErr(err),
                           clientSecret: null, stripePromise: null,
                           company_name: null, customer_email_hint: null });
            }
        })();
    }, [token, alreadyDone]);

    return (
        <div className="min-h-screen bg-slate-50 flex items-start justify-center pt-16 pb-16 px-4">
            <div className="w-full max-w-lg">
                <div className="flex items-center gap-2 mb-6 text-slate-500 text-sm">
                    <Landmark size={16} className="text-blue-900" />
                    <span>Bank verification</span>
                </div>
                <Card data-testid="activate-ach-card">
                    <CardHeader>
                        <CardTitle className="font-display text-2xl tracking-tight">
                            {succeeded ? "Bank verified" : "Verify your bank"}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {state.loading && (
                            <div className="flex items-center gap-2 text-sm text-slate-500 py-6 justify-center" data-testid="activate-ach-loading">
                                <Loader2 size={14} className="animate-spin" /> Preparing secure verification…
                            </div>
                        )}
                        {!state.loading && state.error && (
                            <div className="rounded-md bg-red-50 ring-1 ring-red-200 p-3 text-sm text-red-800 flex items-start gap-2" data-testid="activate-ach-boot-error">
                                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                                <span>{state.error}</span>
                            </div>
                        )}
                        {!state.loading && !state.error && succeeded && (
                            <div className="space-y-4 text-center py-4" data-testid="activate-ach-success">
                                <CheckCircle2 size={40} className="text-emerald-600 mx-auto" />
                                <p className="text-sm text-slate-700">
                                    Your bank is now verified. Auto-pay is available for future invoices.
                                </p>
                                <Link to="/customer/invoices">
                                    <Button variant="outline" data-testid="activate-ach-portal-link">
                                        Return to portal
                                    </Button>
                                </Link>
                            </div>
                        )}
                        {!state.loading && !state.error && !succeeded && state.clientSecret && (
                            <Elements
                                stripe={state.stripePromise}
                                options={{ clientSecret: state.clientSecret, appearance: { theme: "stripe" } }}
                            >
                                <ActivateInner
                                    boot={state}
                                    onSuccess={() => setSucceeded(true)}
                                />
                            </Elements>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
